import React from 'react';
import LazyLoad from 'react-lazy-load';
import { useSelector } from 'react-redux';

import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';
import { BaseUser } from '@common/react/objects/BaseUser';
import { BaseApplicationState } from '@common/react/store';

import { getImagePath } from '@app/objects/Image';
import '@app/scss/components/customAvatar.scss';

interface CustomAvatarProps {
	src: string | undefined | null;
	alt?: string;
	errorAvatar?: any;
	wrapClassName?: string;
	className?: string;
	maxWidth?: number;
	maxHeight?: number;
	lazy?: boolean;
	offset?: number;
	onClick?: () => void;
}

const defaultVetAvatar = require('@app/images/vetDefAvatar.svg');

const CustomAvatar: React.FC<CustomAvatarProps> = ({
	src,
	alt = 'Avatar',
	errorAvatar = defaultVetAvatar,
	wrapClassName = '',
	className = '',
	maxWidth,
	maxHeight,
	lazy = true,
	offset = 300,
	onClick,
}) => {
	const hostOptions = useSelector((state: BaseApplicationState<BaseUser>) => state.hostOptions.item);
	const [imgSrc, setImgSrc] = React.useState<string>(getImagePath(src, maxHeight, maxWidth));
	const [hasError, setHasError] = React.useState<boolean>(false);
	const [isLoaded, setIsLoaded] = React.useState<boolean>(false);
	const [isTimeout, setIsTimeout] = React.useState<boolean>(false);
	const [isVisible, setIsVisible] = React.useState(!lazy);
	const [timer, setTimer] = React.useState<NodeJS.Timeout | undefined>(undefined);

	const clearTimer = () => {
		if (timer) {
			clearTimeout(timer);
			setTimer(undefined);
		}
		setIsTimeout(false);
	};

	React.useEffect(() => {
		setImgSrc(getImagePath(src, maxHeight, maxWidth, hostOptions?.hostServer || ''));
		setHasError(false);
		setIsLoaded(false);
		setIsTimeout(false);
		setIsVisible(!lazy);
		clearTimer();
	}, [src, maxWidth, maxHeight, lazy]);

	React.useEffect(() => {
		if (isVisible && !isLoaded && !hasError && !timer) {
			const newTimer = setTimeout(() => {
				setIsTimeout(true);
			}, 5000);
			setTimer(newTimer);
		}

		return () => clearTimer();
	}, [isVisible, isLoaded, hasError]);

	const showErrorImage = hasError || isTimeout;
	const showLoader = !isLoaded && !showErrorImage;

	const imageElement = (
		<img
			src={showErrorImage ? errorAvatar : imgSrc}
			style={{ display: isLoaded || showErrorImage ? 'block' : 'none' }}
			className={className}
			alt={alt}
			onLoad={() => {
				setIsLoaded(true);
				clearTimer();
			}}
			onError={() => {
				setHasError(true);
				clearTimer();
			}}
			onClick={() => onClick && onClick()}
		/>
	);

	return (
		<div className={`relative custom-avatar ${wrapClassName}`}>
			{showLoader && <BounceLoader className="loading-component__m0-auto" />}

			{lazy ? (
				<div
					style={{
						visibility: isLoaded || showErrorImage ? 'visible' : 'hidden',
						position: isLoaded || showErrorImage ? 'static' : 'absolute',
						pointerEvents: isLoaded || showErrorImage ? 'auto' : 'none',
					}}
					className="LazyLoad"
				>
					<LazyLoad offset={offset} onContentVisible={() => setIsVisible(true)}>
						{imageElement}
					</LazyLoad>
				</div>
			) : (
				imageElement
			)}
		</div>
	);
};

export default CustomAvatar;
